import React, { useState, useEffect } from 'react';
import { Search, Filter, CheckSquare, Square, Users, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { citySettingsService } from '../../../services/citySettingsService';
import { supabase } from '../../../lib/supabase';

const RecipientSelector = ({ selectedRecipients, onSelectRecipients, onBulkSelect, onCriteriaChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [criteria, setCriteria] = useState({
    status: [],
    city: null,
    ageCategory: [],
    schoolIds: []
  });
  const [cities, setCities] = useState([]);
  const [ageCategories] = useState([
    { value: 'boys_u14', label: 'Boys U14' },
    { value: 'girls_u15', label: 'Girls U15' },
    { value: 'boys_u16', label: 'Boys U16' }
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadCities();
    loadContacts();
  }, []);

  useEffect(() => {
    if (onCriteriaChange) {
      onCriteriaChange(criteria);
    }
  }, [criteria]);

  const loadCities = async () => {
    try {
      const data = await citySettingsService.getAllCitySettings();
      setCities(data.map(c => c.city));
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const filters = {
        status: criteria.status.length > 0 ? criteria.status[0] : 'all',
        city: criteria.city || 'all',
        ageGroup: criteria.ageCategory.length > 0 ? criteria.ageCategory[0] : 'all'
      };
      
      const result = await adminService.getFilteredRegistrations(filters, { page: currentPage, pageSize: pageSize });
      
      // Fetch full registration details to get mobile numbers
      const registrationIds = (result.data || []).map(reg => reg.dbId);
      if (registrationIds.length > 0) {
        const { data: fullRegs } = await supabase
          .from('registrations')
          .select('id, mobile_number, poc_name, poc_email')
          .in('id', registrationIds);
        
        const mobileMap = {};
        const emailMap = {};
        (fullRegs || []).forEach(reg => {
          mobileMap[reg.id] = reg.mobile_number;
          emailMap[reg.id] = reg.poc_email;
        });
        
        const transformed = (result.data || []).map(reg => ({
          id: reg.dbId,
          name: reg.coachName,
          school: reg.schoolName,
          city: reg.city,
          category: reg.ageCategory,
          status: reg.status,
          mobile: mobileMap[reg.dbId] || '+91 0000000000',
          email: emailMap[reg.dbId] || ''
        }));
        
        setContacts(transformed);
        setTotalCount(result.total || 0);
        setTotalPages(Math.ceil((result.total || 0) / pageSize));
      } else {
        setContacts([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [criteria, currentPage, pageSize]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleCriteriaChangeLocal = (field, value) => {
    setCriteria(prev => {
      const newCriteria = { ...prev };
      if (field === 'status') {
        if (Array.isArray(value)) {
          newCriteria.status = value;
        } else {
          newCriteria.status = newCriteria.status.includes(value)
            ? newCriteria.status.filter(s => s !== value)
            : [...newCriteria.status, value];
        }
      } else if (field === 'ageCategory') {
        if (Array.isArray(value)) {
          newCriteria.ageCategory = value;
        } else {
          newCriteria.ageCategory = newCriteria.ageCategory.includes(value)
            ? newCriteria.ageCategory.filter(ac => ac !== value)
            : [...newCriteria.ageCategory, value];
        }
      } else {
        newCriteria[field] = value;
      }
      return newCriteria;
    });
    setCurrentPage(1); // Reset to first page when criteria changes
  };

  const handleSelectContact = (contact) => {
    const isSelected = selectedRecipients?.some(r => r?.id === contact?.id);
    if (isSelected) {
      onSelectRecipients(selectedRecipients?.filter(r => r?.id !== contact?.id));
    } else {
      onSelectRecipients([...selectedRecipients, contact]);
    }
  };

  const handleBulkSelectByCriteria = () => {
    const filtered = contacts.filter(contact => {
      if (criteria.status.length > 0 && !criteria.status.includes(contact.status)) return false;
      if (criteria.city && contact.city !== criteria.city) return false;
      if (criteria.ageCategory.length > 0) {
        const categoryMatch = criteria.ageCategory.some(ac => {
          const map = { 'boys_u14': 'Boys U14', 'girls_u15': 'Girls U15', 'boys_u16': 'Boys U16' };
          return contact.category === map[ac];
        });
        if (!categoryMatch) return false;
      }
      return true;
    });
    
    onBulkSelect({ criteria, recipients: filtered });
  };


  const filteredContacts = contacts?.filter(contact => {
    const matchesSearch = contact?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         contact?.school?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = activeFilter === 'all' || 
                         contact?.status === activeFilter ||
                         contact?.category?.toLowerCase() === activeFilter;
    return matchesSearch && matchesFilter;
  });

  const statusOptions = [
    { id: 'draft', label: 'Draft', count: contacts.filter(c => c.status === 'draft').length },
    { id: 'submitted', label: 'Submitted', count: contacts.filter(c => c.status === 'submitted').length },
    { id: 'approved', label: 'Approved', count: contacts.filter(c => c.status === 'approved').length },
    { id: 'rejected', label: 'Rejected', count: contacts.filter(c => c.status === 'rejected').length },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Recipients</h3>
          <span className="text-sm text-gray-600">{selectedRecipients?.length} selected</span>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 mt-3 text-sm text-blue-600 hover:text-blue-700"
        >
          <Filter className="w-4 h-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Criteria Builder */}
      {showFilters && (
        <div className="p-4 border-b border-gray-200 bg-gray-50 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-2 block">Status</label>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => handleCriteriaChangeLocal('status', option.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    criteria.status.includes(option.id)
                      ? 'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {option.label} ({option.count})
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 mb-2 block">City</label>
            <select
              value={criteria.city || ''}
              onChange={(e) => handleCriteriaChangeLocal('city', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">All Cities</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 mb-2 block">Age Category</label>
            <div className="flex flex-wrap gap-2">
              {ageCategories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => handleCriteriaChangeLocal('ageCategory', cat.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    criteria.ageCategory.includes(cat.value)
                      ? 'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleBulkSelectByCriteria}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Users className="w-4 h-4" />
            Select All Matching ({filteredContacts.length})
          </button>
        </div>
      )}

      {/* Contact List */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No contacts found</div>
        ) : (
          <div className="space-y-2">
            {filteredContacts.map(contact => {
              const isSelected = selectedRecipients?.some(r => r?.id === contact?.id);
              return (
                <div
                  key={contact?.id}
                  onClick={() => handleSelectContact(contact)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-blue-50 border-blue-300' :'bg-white border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {isSelected ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{contact?.name}</h4>
                      <p className="text-sm text-gray-600 truncate">{contact?.school}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{contact?.city}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">{contact?.category}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          contact?.status === 'approved' ?'bg-green-100 text-green-700'
                            : contact?.status === 'pending' ?'bg-yellow-100 text-yellow-700' :'bg-orange-100 text-orange-700'
                        }`}>
                          {contact?.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{contact?.mobile}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-600">per page</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs sm:text-sm text-gray-700 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientSelector;
